import { useMemo } from 'react';
import { getStatusCopy } from '@/shared/copy/status';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { r, s, typographyTokens, typeScale } from '@/theme/tokens';
import { Box, Text, useTheme } from '@/ui/primitives';
import { formatChartTooltipDateLabel } from '@/ui/primitives/chartDateLabel';
import { NorthIcon, SouthIcon } from '@/ui/primitives/icons';
import { MetricTrendCardFrame } from './MetricTrendCardFrame';
import { metricTrendCardCompactLayout } from './MetricTrendCardFrame.layout';
import { MiniMetricLineChart } from './MiniMetricLineChart';
import {
  buildMetricCardGridColumns,
  buildMetricCardGridPlacement,
} from './StatusMetricCardGridLayout';
import { StatusMetricCardsGridSkeletons } from './StatusMetricCardsGridSkeletons';
import type { MetricsStatusChartPanelDisplayModel } from './useMetricsStatusChartPanelModel';
import type { MiniMetricLineChartDatum } from '@/features/executions/utils/chartData';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { ElementType, ReactElement, ReactNode } from 'react';

type StatusMetricCardsGridProps = {
  metricCards: MetricCardItem[];
  loading?: boolean;
  skeletonCount?: number;
  comparisonWindowLabel: string | null;
  showNoChangePlaceholder?: boolean;
  resolveStatusCardIcon: (statusId: string) => ElementType;
  resolveStatusCardIconColor: (statusTone: MetricCardItem['statusTone']) => string;
  statusChartData?: MetricsStatusChartPanelDisplayModel['statusChartData'];
  selectedIds?: string[];
  onCardToggle?: (statusId: string) => void;
  maxDesktopColumns?: number;
};

type TrendDatum = {
  label: string;
  value: number | null;
};

const statusMetricCardMinHeight = metricTrendCardCompactLayout.minHeight;
const statusMetricCardChartHeight = metricTrendCardCompactLayout.chartHeight;

const isDisplayableNode = (value: ReactNode): boolean =>
  value !== null && value !== undefined && value !== '' && value !== '-';

const isZeroLikeDeltaNode = (value: ReactNode): boolean => {
  if (typeof value === 'number') return Object.is(value, 0) || Object.is(value, -0);
  if (typeof value !== 'string') return false;

  const normalisedValue = value.trim().replaceAll(',', '').replace(/%$/, '');
  return /^[+-]?0(?:\.0+)?$/.test(normalisedValue);
};

const isNoChangeCountDeltaNode = (value: ReactNode): boolean =>
  value === '-' || isZeroLikeDeltaNode(value);

const isMissingMetricCard = (card: MetricCardItem): boolean =>
  card.isMissing === true || card.count === getStatusCopy('noData');

const isZeroMetricCardCount = (value: ReactNode): boolean => {
  if (typeof value === 'number') return Object.is(value, 0);
  if (typeof value !== 'string') return false;
  return /^0(?:\.0+)?$/.test(value.trim().replaceAll(',', ''));
};

const shouldHideMetricCard = (card: MetricCardItem): boolean =>
  card.id === 'errored' && card.isMissing !== true && isZeroMetricCardCount(card.count);

const buildStatusTrendData = (
  cardId: string,
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'] | undefined,
): TrendDatum[] => {
  if (!statusChartData) return [];

  return statusChartData.data.map((row) => {
    const value =
      cardId === 'total'
        ? statusChartData.series.reduce((sum, seriesItem) => {
            const seriesValue = row[seriesItem.key];
            return (
              sum +
              (typeof seriesValue === 'number' && Number.isFinite(seriesValue) ? seriesValue : 0)
            );
          }, 0)
        : row[cardId];

    return {
      label: String(row.label),
      value: typeof value === 'number' && Number.isFinite(value) ? value : null,
    };
  });
};

const hasVisibleTrendValue = (data: TrendDatum[]): boolean =>
  data.some((point) => typeof point.value === 'number' && Number.isFinite(point.value));

const buildMiniMetricTrendData = (data: TrendDatum[]): MiniMetricLineChartDatum[] => {
  let carryForwardValue = 0;

  return data.map((point) => {
    if (typeof point.value === 'number' && Number.isFinite(point.value)) {
      carryForwardValue = point.value;
    }

    return {
      label: point.label,
      measuredValue: point.value,
      carryForwardValue,
    };
  });
};

const resolveYMax = (data: TrendDatum[]): number => {
  const maxValue = data.reduce(
    (max, point) =>
      typeof point.value === 'number' && Number.isFinite(point.value) && point.value > max
        ? point.value
        : max,
    0,
  );
  if (maxValue <= 0) return 1;
  return maxValue * 1.18;
};

const renderMetricValue = ({
  count,
  percentage,
  secondaryValue,
  isMissing,
}: {
  count: MetricCardItem['count'];
  percentage: MetricCardItem['percentage'];
  secondaryValue: MetricCardItem['secondaryValue'];
  isMissing: boolean;
}): ReactElement => (
  <Box
    sx={{
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: s('sm'),
      rowGap: s('xxs'),
    }}
  >
    <Text
      role="sectionTitle"
      sx={{
        color: isMissing ? 'text.secondary' : undefined,
        fontSize: isMissing ? { xs: 16, xl: 18 } : { xs: 20, xl: 22 },
        lineHeight: 1.15,
        fontWeight: typographyTokens.body.fontWeight,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {count}
    </Text>
    {isDisplayableNode(percentage) ? (
      <Text
        role="bodyMuted"
        sx={{
          color: 'text.secondary',
          fontSize: typeScale.lg,
          lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {percentage}
      </Text>
    ) : null}
    {isDisplayableNode(secondaryValue) ? (
      <Text
        role="bodyMuted"
        sx={{
          color: 'text.secondary',
          fontSize: typeScale.lg,
          lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {secondaryValue}
      </Text>
    ) : null}
  </Box>
);

const renderStatusCardDeltaText = (
  value: MetricCardItem['countDelta'],
  tone: MetricCardItem['countDeltaTone'],
  wrapAbsoluteValue = false,
  preserveWrappedSign = false,
): ReactElement | null => {
  if (!isDisplayableNode(value)) return null;
  const displayValue =
    wrapAbsoluteValue && (typeof value === 'string' || typeof value === 'number')
      ? `(${preserveWrappedSign ? String(value) : String(value).replace(/^[+-]/, '')})`
      : value;

  return (
    <Text
      role="bodyMuted"
      sx={{
        color: resolveDeltaColor(tone),
        fontSize: typographyTokens.body.fontSize,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      {displayValue}
    </Text>
  );
};

const resolveDeltaColor = (tone: MetricCardItem['countDeltaTone']): string => {
  if (tone === 'positive') return 'success.main';
  if (tone === 'negative') return 'error.main';
  return 'text.secondary';
};

const resolveDeltaArrowDirection = (
  value: MetricCardItem['countDelta'],
  tone: MetricCardItem['countDeltaTone'],
): 'up' | 'down' => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 0 ? 'down' : 'up';
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (trimmedValue.startsWith('-')) return 'down';
    if (trimmedValue.startsWith('+')) return 'up';
  }

  return tone === 'negative' ? 'down' : 'up';
};

export const StatusMetricCardsGrid = ({
  metricCards,
  loading = false,
  skeletonCount,
  comparisonWindowLabel: _comparisonWindowLabel,
  showNoChangePlaceholder = true,
  resolveStatusCardIcon,
  resolveStatusCardIconColor,
  statusChartData,
  selectedIds,
  onCardToggle,
  maxDesktopColumns,
}: StatusMetricCardsGridProps): ReactElement => {
  const theme = useTheme();
  const accentColor = resolveSemanticColor(theme, 'accent');
  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
  const visibleMetricCards = useMemo(
    () => metricCards.filter((card) => !shouldHideMetricCard(card)),
    [metricCards],
  );

  if (loading) {
    return (
      <StatusMetricCardsGridSkeletons
        count={skeletonCount ?? Math.max(metricCards.length, 5)}
        maxDesktopColumns={maxDesktopColumns}
      />
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: buildMetricCardGridColumns(
          visibleMetricCards.length,
          maxDesktopColumns,
        ),
        gridAutoRows: `${statusMetricCardMinHeight}px`,
        columnGap: s('xl'),
        rowGap: s('xl'),
      }}
    >
      {visibleMetricCards.map((card, index) => {
        const countDelta = card.countDelta ?? card.delta;
        const countDeltaTone = card.countDeltaTone ?? card.deltaTone;
        const cardToggle = onCardToggle;
        const isSelectable = cardToggle !== undefined && card.id !== 'total';
        const isSelected = selectedSet.has(card.id);
        const trendData = buildStatusTrendData(card.id, statusChartData);
        const chartData = buildMiniMetricTrendData(trendData);
        const isMissing = isMissingMetricCard(card);
        const hasTrendData = isMissing ? false : hasVisibleTrendValue(trendData);
        const resolvedChartColor = resolveStatusCardIconColor(card.statusTone);
        const resolvedIconColor = isMissing ? 'text.secondary' : resolvedChartColor;
        const StatusIcon = resolveStatusCardIcon(card.id);
        const deltaNode = renderStatusCardDeltaText(countDelta, countDeltaTone);
        const percentageDeltaNode = renderStatusCardDeltaText(
          card.percentageDelta,
          card.percentageDeltaTone,
          true,
          card.percentageDeltaTone !== undefined && card.percentageDeltaTone !== countDeltaTone,
        );
        const hasNoChangeCountDelta =
          showNoChangePlaceholder &&
          isNoChangeCountDeltaNode(countDelta) &&
          (percentageDeltaNode !== null || isZeroLikeDeltaNode(card.percentageDelta));
        const hasDeltaIcon = deltaNode !== null || percentageDeltaNode !== null;
        const deltaIconTone = countDeltaTone ?? card.percentageDeltaTone;
        const deltaIconDirection = resolveDeltaArrowDirection(
          countDelta ?? card.percentageDelta,
          deltaIconTone,
        );
        const valueNode = renderMetricValue({
          count: card.count,
          percentage: card.percentage,
          secondaryValue: card.secondaryValue,
          isMissing,
        });
        const chartNode = !hasTrendData ? (
          <Box
            sx={{
              flex: 1,
              minHeight: statusMetricCardChartHeight,
              borderRadius: r('sm'),
              backgroundColor: (muiTheme) => muiTheme.app.colors.surface,
            }}
          />
        ) : (
          <MiniMetricLineChart
            data={chartData}
            metricKey={card.id}
            color={resolvedChartColor}
            height={statusMetricCardChartHeight}
            marginLeft={0}
            marginRight={8}
            marginTop={4}
            marginBottom={0}
            yAxisWidth={0}
            xAxisHeight={0}
            yAxisMin={0}
            yAxisMax={resolveYMax(trendData)}
            hideXAxisLabels
            hideYAxisLabels
            hideXAxisTicks
            hideYAxisTicks
            hideXAxisLine
            hideYAxisLine
            tooltipLabelFormatter={formatChartTooltipDateLabel}
          />
        );
        const noChangeCountNode = (
          <Text
            role="bodyMuted"
            sx={{
              color: 'text.secondary',
              fontSize: typographyTokens.body.fontSize,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            No change
          </Text>
        );
        const footerNode = hasNoChangeCountDelta ? (
          <>
            {noChangeCountNode}
            {isZeroLikeDeltaNode(card.percentageDelta) ? null : percentageDeltaNode}
          </>
        ) : hasDeltaIcon ? (
          <>
            <Box
              component="span"
              sx={{
                color: resolveDeltaColor(deltaIconTone),
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
              }}
            >
              {deltaIconDirection === 'down' ? (
                <SouthIcon sx={{ fontSize: 16, strokeWidth: 0.5 }} />
              ) : (
                <NorthIcon sx={{ fontSize: 16, strokeWidth: 0.5 }} />
              )}
            </Box>
            {deltaNode}
            {percentageDeltaNode}
          </>
        ) : (
          <Text
            role="bodyMuted"
            sx={{
              color: 'text.secondary',
              fontSize: typographyTokens.body.fontSize,
              lineHeight: 1.2,
            }}
          >
            -
          </Text>
        );
        const interaction = isSelectable
          ? {
              selected: isSelected,
              onClick: (): void => {
                cardToggle(card.id);
              },
              focusColor: accentColor,
            }
          : undefined;

        return (
          <MetricTrendCardFrame
            key={`${card.id}-${String(index)}`}
            title={card.status}
            cornerIcon={
              <StatusIcon
                sx={{
                  color: resolvedIconColor,
                  fontSize: 22,
                }}
              />
            }
            minHeight={statusMetricCardMinHeight}
            chartHeight={statusMetricCardChartHeight}
            fixedHeight
            sx={buildMetricCardGridPlacement(visibleMetricCards.length, index, maxDesktopColumns)}
            headerPaddingX={metricTrendCardCompactLayout.headerPaddingX}
            headerPaddingTop={metricTrendCardCompactLayout.headerPaddingTop}
            headerPaddingBottom={metricTrendCardCompactLayout.headerPaddingBottom}
            middleGridTemplateColumns={metricTrendCardCompactLayout.middleGridTemplateColumns}
            middleColumnGap={metricTrendCardCompactLayout.middleColumnGap}
            middlePaddingY={metricTrendCardCompactLayout.middlePaddingY}
            value={valueNode}
            valuePaddingX={metricTrendCardCompactLayout.valuePaddingX}
            valuePaddingRight={metricTrendCardCompactLayout.valuePaddingRight}
            valuePaddingY={metricTrendCardCompactLayout.valuePaddingY}
            chart={chartNode}
            chartPaddingX={metricTrendCardCompactLayout.chartPaddingX}
            chartPaddingLeft={metricTrendCardCompactLayout.chartPaddingLeft}
            footer={footerNode}
            footerPaddingX={metricTrendCardCompactLayout.footerPaddingX}
            footerPaddingTop={metricTrendCardCompactLayout.footerPaddingTop}
            footerPaddingBottom={metricTrendCardCompactLayout.footerPaddingBottom}
            footerSx={{
              columnGap: s('sm'),
              rowGap: s('xxs'),
            }}
            interaction={interaction}
          />
        );
      })}
    </Box>
  );
};
